import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';
import { addDoc, collection, Timestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import type { Group } from '@/types/support';
import { postAuthorFields } from '@/lib/groups/groupPosts';
import { updateGroupLastPost } from '@/lib/firebase/queries/groups';
import { cn } from '@/lib/utils';

interface CreatePostProps {
  group: Group;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function CreatePost({ group, open, onOpenChange, onSuccess }: CreatePostProps) {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);
  const { t, language } = useLanguage();
  const { currentUser, userData } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser || !userData) return;

    try {
      setLoading(true);
      const postData = {
        title,
        content,
        userId: currentUser.uid,
        // Same author fields as mobile (namat#36 P2).
        ...postAuthorFields(userData, language),
        groupId: group.id,
        category: group.categories?.[0] || 'general',
        likes: 0,
        likedBy: [],
        comments: 0,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      await addDoc(collection(db, 'groupPosts'), postData);
      // Keep the group card's "last post" preview current, like mobile.
      await updateGroupLastPost(group.id);
      toast.success(t('support.postCreated'));
      onSuccess?.();
      onOpenChange(false);
    } catch (error) {
      console.error('Error creating post:', error);
      toast.error(t('support.postCreateError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
              <DialogContent className="w-screen h-[100dvh] sm:w-[80vw] sm:max-w-lg sm:h-auto" dir={language === 'ar' ? 'rtl' : 'ltr'}>
        <DialogHeader>
          <DialogTitle>{t('support.createPost')}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="title">{t('support.postTitle')}</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t('support.postTitlePlaceholder')}
              className={cn("text-base", language === 'ar' ? "text-right" : "text-left")}
              style={{ fontSize: '16px' }}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="content">{t('support.postContent')}</Label>
            <Textarea
              id="content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder={t('support.postContentPlaceholder')}
              className={cn("min-h-[200px] text-base", language === 'ar' ? "text-right" : "text-left")}
              style={{ fontSize: '16px' }}
              required
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              disabled={loading || !title.trim() || !content.trim()}
              className="bg-[#7dda55] hover:bg-[#6dc447] text-white"
            >
              {loading ? (
                <>
                  <Loader2 className={cn("h-4 w-4 animate-spin", language === 'ar' ? "ml-2" : "mr-2")} />
                  {t('common.creating')}
                </>
              ) : (
                t('common.create')
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}